import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Swal from 'sweetalert2';

import { deleteRequest, getRequest, postRequest } from 'Includes/api/apicall';
import axiosAPI from 'Includes/api/api';
import { DEL_URL, GET_URL, POST_URL } from 'Includes/urls';

const INSTITUTE_TYPES = [
  { id: 'school', label: 'School' },
  { id: 'nursing', label: 'Nursing' },
  { id: 'college', label: 'College' },
  { id: 'engineering', label: 'Engineering' },
];

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.m4v,.avi';
const PDF_ACCEPT = 'application/pdf,.pdf';

function typeLabel(mediaType) {
  if (mediaType === 'video') return 'Video';
  if (mediaType === 'pdf') return 'PDF';
  return 'Photo';
}

function emptyTextLink() {
  return { text: '', link: '', link_cover: '', align: 'center', vertical: 'bottom' };
}

function itemsFromRow(row) {
  if (Array.isArray(row.text_links) && row.text_links.length) {
    return row.text_links.map((item) => ({ ...emptyTextLink(), ...item }));
  }
  if (row.overlay_text || row.link_url || row.link_cover) {
    return [{
      text: row.overlay_text || '',
      link: row.link_url || '',
      link_cover: row.link_cover || '',
      align: 'center',
      vertical: 'bottom',
    }];
  }
  return [emptyTextLink()];
}

function AttachmentFields({ row, onSave }) {
  const [items, setItems] = React.useState(() => itemsFromRow(row));

  React.useEffect(() => {
    setItems(itemsFromRow(row));
  }, [row.id, row.text_links, row.overlay_text, row.link_url, row.link_cover]);

  const updateItem = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  return (
    <Box sx={{ mt: 1 }} onMouseDown={(event) => event.stopPropagation()}>
      {items.map((item, index) => (
        <Box key={`${row.id}-${index}`} sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="Text" value={item.text} onChange={(event) => updateItem(index, 'text', event.target.value)} sx={{ minWidth: 120 }} />
          <TextField size="small" label="Link" value={item.link} onChange={(event) => updateItem(index, 'link', event.target.value)} sx={{ minWidth: 160 }} />
          <TextField size="small" label="Link cover" value={item.link_cover} onChange={(event) => updateItem(index, 'link_cover', event.target.value)} sx={{ minWidth: 120 }} />
          <TextField select size="small" label="Align" value={item.align || 'center'} onChange={(event) => updateItem(index, 'align', event.target.value)} sx={{ minWidth: 110 }}>
            <MenuItem value="left">Left</MenuItem>
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="right">Right</MenuItem>
          </TextField>
          <TextField select size="small" label="Position" value={item.vertical || 'bottom'} onChange={(event) => updateItem(index, 'vertical', event.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="top">Top</MenuItem>
            <MenuItem value="middle">Middle</MenuItem>
            <MenuItem value="bottom">Bottom</MenuItem>
          </TextField>
          <IconButton aria-label="Remove text" disabled={items.length === 1} onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ))}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" onClick={() => setItems((current) => [...current, emptyTextLink()])}>Add text</Button>
        <Button size="small" variant="outlined" onClick={() => onSave(row, items)}>Save</Button>
      </Box>
    </Box>
  );
}

function mediaUrl(row) {
  return row?.file_url || row?.file_link || row?.file || '';
}

function appChecked(value) {
  return value !== false && value !== 0 && value !== '0' && value !== 'false';
}

function AdRow({ row, index, total, onMove, onDelete, actionLabel, onAction, drag, onSaveAttachment, onAudience }) {
  const url = mediaUrl(row);
  const isVideo = row.media_type === 'video';
  const isPdf = row.media_type === 'pdf';
  const showOrder = typeof index === 'number';
  return (
    <Paper
      draggable={Boolean(drag)}
      onDragStart={drag?.onDragStart}
      onDragOver={drag?.onDragOver}
      onDrop={drag?.onDrop}
      onDragEnd={drag?.onDragEnd}
      sx={{
        display: 'flex',
        overflow: 'hidden',
        alignItems: 'stretch',
        cursor: drag ? 'grab' : 'default',
        opacity: drag?.dragging ? 0.45 : 1,
        outline: drag?.over ? '2px solid #2563eb' : 'none',
        outlineOffset: drag?.over ? -2 : 0,
      }}
    >
      {showOrder && (
        <Box
          sx={{
            width: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.25,
            fontWeight: 700,
            fontSize: 18,
            bgcolor: drag?.over ? '#dbeafe' : '#e2e8f0',
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18, color: '#64748b' }} />
          {index + 1}
        </Box>
      )}
      <Box sx={{ width: 180, height: 110, bgcolor: '#0f172a', flexShrink: 0 }}>
        {isVideo && url ? (
          <video draggable={false} src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : isPdf ? (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexDirection: 'column', gap: 0.5 }}>
            <PictureAsPdfOutlinedIcon />
            <Typography variant="caption">PDF</Typography>
          </Box>
        ) : url ? (
          <img draggable={false} src={url} alt={row.title || 'Ad photo'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : null}
      </Box>
      <Box sx={{ p: 1.5, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap fontWeight={600}>
            {row.title || typeLabel(row.media_type)}
          </Typography>
          <Chip size="small" label={typeLabel(row.media_type)} sx={{ mt: 0.5 }} />
          {onAudience && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} onMouseDown={(event) => event.stopPropagation()}>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={appChecked(row.show_student_app)}
                    onChange={(event) => onAudience(row, 'show_student_app', event.target.checked)}
                  />
                )}
                label="Student app"
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={appChecked(row.show_staff_app)}
                    onChange={(event) => onAudience(row, 'show_staff_app', event.target.checked)}
                  />
                )}
                label="Staff app"
              />
            </Box>
          )}
          <AttachmentFields row={row} onSave={onSaveAttachment} />
        </Box>
        {showOrder && (
          <>
            <IconButton aria-label="Move up" disabled={index === 0} onClick={() => onMove(index, -1)}>
              <ArrowUpwardIcon />
            </IconButton>
            <IconButton aria-label="Move down" disabled={index === total - 1} onClick={() => onMove(index, 1)}>
              <ArrowDownwardIcon />
            </IconButton>
          </>
        )}
        <Button variant={showOrder ? 'outlined' : 'contained'} size="small" onClick={onAction}>
          {actionLabel}
        </Button>
        <IconButton aria-label="Delete ad" onClick={() => onDelete(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}

function EdubriczAds(props) {
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState('');
  const [title, setTitle] = useState('');
  const [menuTab, setMenuTab] = useState('ads');
  const [instituteType, setInstituteType] = useState('school');
  const [placement, setPlacement] = useState('login');
  const [dragId, setDragId] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [interests, setInterests] = useState([]);

  const loadAds = useCallback(() => {
    setLoading(true);
    const params = {
      limit: 100,
      pageno: 1,
      popup: placement === 'popup' ? 1 : 0,
    };
    if (placement !== 'popup') params.institute_type = instituteType;
    getRequest(GET_URL.edubricz_ads.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          const data = response.data?.data || [];
          setAds(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => setLoading(false));
  }, [props, instituteType, placement]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  useEffect(() => {
    getRequest(`${GET_URL.edubricz_ads.api}interests/`, {}, props).then((response) => {
      if (response && response.status === 200) {
        const data = response.data?.data || [];
        setInterests(Array.isArray(data) ? data : []);
      }
    });
  }, [props]);

  const uploadFile = (file, mediaType) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', mediaType);
    if (title.trim()) formData.append('title', title.trim());
    formData.append('institute_type', instituteType);
    if (placement === 'popup') formData.append('is_popup', '1');

    setUploading(mediaType);
    axiosAPI.post(POST_URL.edubricz_ads.api, formData, {
      headers: { 'Content-Type': undefined },
    })
      .then((response) => {
        if (response && (response.status === 200 || response.status === 201)) {
          Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: mediaType === 'video' ? 'Video uploaded' : mediaType === 'pdf' ? 'PDF uploaded' : 'Photo uploaded',
            showConfirmButton: false,
            timer: 1400,
          });
          setTitle('');
          if (mediaType === 'photo' && photoInputRef.current) photoInputRef.current.value = '';
          if (mediaType === 'video' && videoInputRef.current) videoInputRef.current.value = '';
          if (mediaType === 'pdf' && pdfInputRef.current) pdfInputRef.current.value = '';
          loadAds();
          return;
        }
        const detail = response?.data?.file || response?.data?.detail || response?.data?.error;
        const message = Array.isArray(detail) ? detail.join(' ') : (detail || 'Upload failed');
        Swal.fire({ icon: 'error', title: 'Upload failed', text: String(message) });
      })
      .catch((error) => {
        const response = error.response;
        const detail = response?.data?.file || response?.data?.detail || response?.data?.error;
        const message = Array.isArray(detail) ? detail.join(' ') : (detail || 'Upload failed');
        Swal.fire({ icon: 'error', title: 'Upload failed', text: String(message) });
      })
      .finally(() => setUploading(''));
  };

  const shownAds = ads.filter((row) => row.is_active);
  const libraryAds = ads.filter((row) => !row.is_active);

  const persistShownOrder = (orderedShown, addedId) => {
    const saveOrder = () => postRequest(
      `${POST_URL.edubricz_ads.api}reorder/`,
      { ids: orderedShown.map((row) => row.id), institute_type: instituteType, popup: placement === 'popup' ? 1 : 0 },
      props
    );
    if (!addedId) {
      saveOrder();
      return;
    }
    postRequest(
      `${POST_URL.edubricz_ads.api}selection/`,
      { id: addedId, show: true },
      props
    ).then((response) => {
      if (response && response.status === 200) saveOrder();
    });
  };

  const commitShownOrder = (orderedShown, addedId) => {
    const shownIds = new Set(orderedShown.map((row) => row.id));
    setAds([
      ...orderedShown,
      ...ads.filter((row) => !shownIds.has(row.id)),
    ]);
    persistShownOrder(orderedShown, addedId);
  };

  const moveAd = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= shownAds.length) return;
    const next = [...shownAds];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    commitShownOrder(next.map((row, i) => ({ ...row, display_order: i + 1, is_active: true })));
  };

  const clearDrag = () => {
    setDragId(null);
    setOverIndex(null);
  };

  const startDrag = (row) => (event) => {
    event.dataTransfer.setData('text/plain', String(row.id));
    event.dataTransfer.effectAllowed = 'move';
    setDragId(row.id);
  };

  const allowDrop = (index) => (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };

  const dropOnShown = (index) => (event) => {
    event.preventDefault();
    const id = Number(event.dataTransfer.getData('text/plain'));
    const source = ads.find((row) => Number(row.id) === id);
    clearDrag();
    if (!source) return;
    const without = shownAds.filter((row) => Number(row.id) !== id);
    const insertAt = Math.max(0, Math.min(index, without.length));
    const next = [...without];
    next.splice(insertAt, 0, { ...source, is_active: true });
    commitShownOrder(
      next.map((row, i) => ({ ...row, display_order: i + 1, is_active: true })),
      source.is_active ? null : source.id
    );
  };

  const dropOnLibrary = (event) => {
    event.preventDefault();
    const id = Number(event.dataTransfer.getData('text/plain'));
    const source = ads.find((row) => Number(row.id) === id);
    clearDrag();
    if (!source || !source.is_active) return;
    setAds(ads.map((row) => (Number(row.id) === id ? { ...row, is_active: false } : row)));
    setShown(source, false);
  };

  const setShown = (row, show) => {
    postRequest(
      `${POST_URL.edubricz_ads.api}selection/`,
      { id: row.id, show },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        loadAds();
      }
    });
  };

  const saveAudience = (row, field, checked) => {
    const next = {
      show_student_app: field === 'show_student_app' ? checked : appChecked(row.show_student_app),
      show_staff_app: field === 'show_staff_app' ? checked : appChecked(row.show_staff_app),
    };
    if (!next.show_student_app && !next.show_staff_app) {
      Swal.fire({ icon: 'info', title: 'Choose the student app, the staff app, or both' });
      return;
    }
    setAds((current) => current.map((item) => (item.id === row.id ? { ...item, ...next } : item)));
    postRequest(
      `${POST_URL.edubricz_ads.api}audience/`,
      { id: row.id, ...next },
      { ...props, return_error: true }
    ).then((response) => {
      if (response && response.status === 200) return;
      const detail = response?.data?.detail || 'Could not save app choice';
      Swal.fire({ icon: 'error', title: 'Save failed', text: String(detail) });
      loadAds();
    });
  };

  const saveAttachment = (row, items) => {
    postRequest(
      `${POST_URL.edubricz_ads.api}details/`,
      { id: row.id, items },
      { ...props, return_error: true }
    ).then((response) => {
      if (response && response.status === 200) {
        loadAds();
        return;
      }
      const detail = response?.data?.detail || 'Could not save text or link';
      Swal.fire({ icon: 'error', title: 'Save failed', text: String(detail) });
    });
  };

  const handleDelete = (row) => {
    deleteRequest(`${DEL_URL.edubricz_ads.api}${row.id}/`, {}, props).then((res) => {
      if (res?.status === 200) {
        Swal.fire({
          position: 'top-end',
          icon: 'success',
          title: 'Deleted',
          showConfirmButton: false,
          timer: 1200,
        });
        loadAds();
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
        Edubricz ads
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {menuTab === 'interest'
          ? 'People who tapped Interested on a login ad, with the contact saved from their sign-in.'
          : 'Login ads are separate for each institute type. Popup ads are one shared set for every institute type, and the ads API returns them only when popup=1.'}
      </Typography>
      <Tabs value={menuTab} onChange={(_event, value) => setMenuTab(value)} sx={{ mb: 2 }}>
        <Tab value="ads" label="Ads" />
        <Tab value="interest" label="Interest" />
      </Tabs>
      {menuTab === 'interest' ? (
        <InterestList rows={interests} />
      ) : (
      <>
      <Tabs
        value={placement}
        onChange={(_event, value) => {
          setPlacement(value);
          setAds([]);
          setDragId(null);
          setOverIndex(null);
        }}
        sx={{ mb: 2 }}
      >
        <Tab value="login" label="Login ads" />
        <Tab value="popup" label="Popup ads" />
      </Tabs>
      {placement !== 'popup' && (
      <Tabs
        value={instituteType}
        onChange={(_event, value) => {
          setInstituteType(value);
          setAds([]);
          setDragId(null);
          setOverIndex(null);
        }}
        sx={{ mb: 2 }}
      >
        {INSTITUTE_TYPES.map((item) => (
          <Tab key={item.id} value={item.id} label={item.label} />
        ))}
      </Tabs>
      )}

      <Paper sx={{ p: 2.5, mb: 3 }}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2, maxWidth: 480 }}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                border: '1px dashed #94a3b8',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <ImageOutlinedIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>Upload photo</Typography>
                <Typography variant="caption" color="text.secondary">
                  JPG, PNG, GIF, or WEBP. Up to 20 MB.
                </Typography>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept={PHOTO_ACCEPT}
                  hidden
                  onChange={(e) => uploadFile(e.target.files?.[0], 'photo')}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={Boolean(uploading)}
                onClick={() => photoInputRef.current?.click()}
              >
                {uploading === 'photo' ? 'Uploading...' : 'Photo'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                border: '1px dashed #94a3b8',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <VideocamOutlinedIcon color="secondary" />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>Upload video</Typography>
                <Typography variant="caption" color="text.secondary">
                  MP4, WEBM, MOV, or AVI. Up to 200 MB.
                </Typography>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={VIDEO_ACCEPT}
                  hidden
                  onChange={(e) => uploadFile(e.target.files?.[0], 'video')}
                />
              </Box>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={Boolean(uploading)}
                onClick={() => videoInputRef.current?.click()}
              >
                {uploading === 'video' ? 'Uploading...' : 'Video'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                border: '1px dashed #94a3b8',
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <PictureAsPdfOutlinedIcon color="error" />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600}>Upload PDF</Typography>
                <Typography variant="caption" color="text.secondary">
                  Shown one page at a time. Up to 30 MB.
                </Typography>
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept={PDF_ACCEPT}
                  hidden
                  onChange={(e) => uploadFile(e.target.files?.[0], 'pdf')}
                />
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<CloudUploadOutlinedIcon />}
                disabled={Boolean(uploading)}
                onClick={() => pdfInputRef.current?.click()}
              >
                {uploading === 'pdf' ? 'Uploading...' : 'PDF'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        {placement === 'popup' ? 'Shown as popup' : 'Shown on login'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {placement === 'popup'
          ? 'These popup ads are the same for every institute type. They are sent when the ads API is called with popup=1. Drag a row to change the order.'
          : `Only these play on ${INSTITUTE_TYPES.find((item) => item.id === instituteType)?.label || 'School'} login pages. Each ad can show in the student app, the staff app, or both. Drag a row to change the order.`}
      </Typography>

      {!loading && shownAds.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <Typography color="text.secondary">
            Nothing selected. Choose photos, videos, or PDFs from the uploads below.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {shownAds.map((row, index) => (
          <AdRow
            key={row.id}
            row={row}
            index={index}
            total={shownAds.length}
            onMove={moveAd}
            onDelete={handleDelete}
            actionLabel="Remove"
            onAction={() => setShown(row, false)}
            onSaveAttachment={saveAttachment}
            onAudience={placement === 'popup' ? null : saveAudience}
            drag={{
              dragging: Number(dragId) === Number(row.id),
              over: overIndex === index && Number(dragId) !== Number(row.id),
              onDragStart: startDrag(row),
              onDragOver: allowDrop(index),
              onDrop: dropOnShown(index),
              onDragEnd: clearDrag,
            }}
          />
        ))}
      </Box>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        {loading ? 'Loading uploads...' : 'Uploads'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {placement === 'popup'
          ? 'These stay here until you add them as popup ads.'
          : 'These stay here until you add them to the login page.'}
      </Typography>

      {!loading && ads.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No photos or videos uploaded yet.</Typography>
        </Paper>
      )}

      {!loading && ads.length > 0 && libraryAds.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {placement === 'popup' ? 'Every upload is already a popup ad.' : 'Every upload is already on the login page.'}
          </Typography>
        </Paper>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          minHeight: 72,
          borderRadius: 1,
          outline: overIndex === 'library' ? '2px dashed #2563eb' : 'none',
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (overIndex !== 'library') setOverIndex('library');
        }}
        onDrop={dropOnLibrary}
      >
        {libraryAds.map((row) => (
          <AdRow
            key={row.id}
            row={row}
            onDelete={handleDelete}
            actionLabel={placement === 'popup' ? 'Show as popup' : 'Show on login'}
            onAction={() => setShown(row, true)}
            onSaveAttachment={saveAttachment}
            onAudience={placement === 'popup' ? null : saveAudience}
            drag={{
              dragging: Number(dragId) === Number(row.id),
              onDragStart: startDrag(row),
              onDragEnd: clearDrag,
            }}
          />
        ))}
      </Box>
      </>
      )}
    </Box>
  );
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function personInitial(row) {
  const source = (row.user_name || row.username || 'I').trim();
  return source.charAt(0).toUpperCase();
}

function InterestList({ rows }) {
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? rows.filter((row) => [
      row.user_name,
      row.username,
      row.mobile,
      row.email,
      row.message,
      row.ad_title,
      row.institute_code,
      row.domain,
    ].some((value) => String(value || '').toLowerCase().includes(needle)))
    : rows;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search name, mobile, email, or message"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
        />
        <Chip label={`${visible.length} interest${visible.length === 1 ? '' : 's'}`} color="primary" variant="outlined" />
      </Box>
      {!rows.length ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography fontWeight={600}>No interest notes yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Notes appear here after someone taps Interested on a login ad and signs in.
          </Typography>
        </Paper>
      ) : null}
      {rows.length > 0 && !visible.length ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">No matches for that search.</Typography>
        </Paper>
      ) : null}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {visible.map((row) => (
          <Paper key={row.id} sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#dbeafe',
                  color: '#1d4ed8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {personInitial(row)}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography fontWeight={700}>{row.user_name || row.username || 'Unknown user'}</Typography>
                    {row.username ? (
                      <Typography variant="caption" color="text.secondary">{row.username}</Typography>
                    ) : null}
                  </Box>
                  <Typography variant="caption" color="text.secondary">{formatWhen(row.created)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  <Chip
                    size="small"
                    label={row.mobile || 'Mobile not saved'}
                    component={row.mobile ? 'a' : 'div'}
                    href={row.mobile ? `tel:${row.mobile}` : undefined}
                    clickable={Boolean(row.mobile)}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={row.email || 'Email not saved'}
                    component={row.email ? 'a' : 'div'}
                    href={row.email ? `mailto:${row.email}` : undefined}
                    clickable={Boolean(row.email)}
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: '#f8fafc', borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.message}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                  <Chip size="small" label={row.ad_title || `Ad ${row.ad_id}`} />
                  {row.institute_code ? <Chip size="small" variant="outlined" label={row.institute_code} /> : null}
                  {row.domain ? <Chip size="small" variant="outlined" label={row.domain} /> : null}
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export default EdubriczAds;
